import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { Modal, TimePicker } from 'antd';
import { useParams } from 'react-router-dom';
import { RuleSettings } from '../types';
import { mixGetV1, mixPostV1 } from 'services/mixin';
import { Plus, Trash2, Edit2, XIcon } from 'lucide-react';
import { API_URL } from "env";
import { useTranslation } from 'react-i18next';
import NumberField from "./NumberField";

type getPreEntryProp = any;
type PreTradePlan = any;

function Button(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { className = "", ...rest } = props;
  return <button className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-gray-200 ${className}`} {...rest} />
}

export default function PreTradePlanForm({ settings, value, onSave }: { settings: RuleSettings; value?: PreTradePlan; onSave: (p: PreTradePlan) => void }) {
  const { id } = useParams();
  const { t } = useTranslation();
  const [mood, setMood] = useState(value?.mood || "Calm");
  const [plannedTrades, setPlannedTrades] = useState<number>(value?.plannedTrades ?? 1);
  const [rrTarget, setRrTarget] = useState<number>(value?.rrTarget ?? settings.minRRAllowed);
  const [expectedHighPrice, setExpectedHighPrice] = useState<any>(value?.expectedHighPrice || "");
  const [expectedLowPrice, setExpectedLowPrice] = useState<any>(value?.expectedLowPrice || "");
  const [plannedWindows, setPlannedWindows] = useState<{ start: string; end: string; label?: string }[]>([{ start: "09:00", end: "10:00", label: "Setup A" }]);
  const [notes, setNotes] = useState<{ content: string; active: boolean }[]>(
    value?.notes
      ? Array.isArray(value.notes)
        ? value.notes
        : value.notes.split(',').filter(Boolean).map((content: string) => ({ content, active: false }))
      : [ 
          { content: t('behavioral_performance_day_plan_suggest_1'), active: false }, 
          { content: t('behavioral_performance_day_plan_suggest_2'), active: false }, 
          { content: t('behavioral_performance_day_plan_suggest_3'), active: true } 
        ]
  ); const [getPreEntry, setGetPreEntry] = useState<getPreEntryProp[]>([]);
  const [preTradePlan, setPreTradePlan] = useState<PreTradePlan[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>(value?.symbol || "US30");
  const [newTodo, setNewTodo] = useState<string>('');
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const editValueRef = useRef<string>('');

  const Dialog: React.FC<{
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
  }> = ({ open, onClose, children }) => {
    if (!open) return null;
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}>
          <button onClick={onClose} style={{ float: 'right', cursor: 'pointer' }}>
            <XIcon></XIcon>
          </button>
          {children}
        </div>
      </div>
    );
  };

  const addWindow = () => setPlannedWindows(ws => [...ws, { start: "", end: "", label: "" }]);
  const removeWindow = (idx: number) => setPlannedWindows(ws => ws.filter((_, i) => i !== idx));

  const getPreEntryData = async () => {
    try {
      const res = await mixGetV1(`/api/v1/daily_behavioral_checklist?account_number=${id}&type=daily`);
      if (res.status && Array.isArray(res.data)) {
        setGetPreEntry(res.data);
      } else {
        setGetPreEntry([]);
      }
    } catch (error) {
      console.error('Error fetching pre-entry data:', error);
      setGetPreEntry([]);
    }
  };

  const getPreTradePlan = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/v1/daily_behavioral_checklist?account_number=${id}&type=daily&limit=10&page=1`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();

      let preTradePlanData: PreTradePlan[] = [];
      if (responseData && typeof responseData === 'object' && Array.isArray(responseData.data?.data)) {
        preTradePlanData = responseData.data.data;
      } else {
        console.warn('Dữ liệu pre-trade plan từ API không phải mảng:', responseData);
        setPreTradePlan([]);
        return;
      }

      setPreTradePlan(preTradePlanData);
      // hydrate form from latest record if available
      if (preTradePlanData.length > 0) {
        const latest = preTradePlanData[0];
        hydrateFromLatest(latest);
      }
    } catch (error) {
      console.error('Error fetching pre-trade plan:', error);
      setPreTradePlan([]);
    }
  };

  // Populate form state from latest record's condition
  const hydrateFromLatest = (record: any) => {
    try {
      const condition = record?.condition || {};
      if (condition.mood) setMood(condition.mood);
      if (typeof condition.plannedTrades !== 'undefined') setPlannedTrades(Number(condition.plannedTrades) || 0);
      if (typeof condition.rrTarget !== 'undefined') setRrTarget(Number(condition.rrTarget) || 0);
      if (typeof condition.expectedHighPrice !== 'undefined') setExpectedHighPrice(Number(condition.expectedHighPrice) || 0);
      if (typeof condition.expectedLowPrice !== 'undefined') setExpectedLowPrice(Number(condition.expectedLowPrice) || 0);
      if (condition.symbol) setSelectedSymbol(condition.symbol);
      if (Array.isArray(condition.plannedWindows)) setPlannedWindows(condition.plannedWindows);

      // normalize notes to [{content, active}]
      const rawNotes = condition.notes;
      if (Array.isArray(rawNotes)) {
        const normalized = rawNotes.map((n: any) => (
          typeof n === 'string' ? { content: n.trim(), active: true } : { content: String(n?.content ?? '').trim(), active: Boolean(n?.active) }
        )).filter((x: any) => x && x.content);
        if (normalized.length > 0) setNotes(normalized);
      } else if (typeof rawNotes === 'string') {
        const normalized = rawNotes.split(',').map((s: string) => s.trim()).filter(Boolean).map((s: string) => ({ content: s, active: true }));
        if (normalized.length > 0) setNotes(normalized);
      }
    } catch (_) {
      // ignore hydration errors
    }
  };

  useEffect(() => {
    getPreEntryData();
    getPreTradePlan();
  }, []);

  function formatTo12Hour(timeString: string): string {
    return dayjs(timeString).format("h:mm:ss A");
  }

  const handleAddTodo = () => {
    if (newTodo.trim()) {
      setNotes([...notes, { content: newTodo.trim(), active: false }]);
      setNewTodo('');
    }
  };
  const handleEditTodo = (index: number) => {
    setEditIndex(index);
    const content = notes[index].content;
    setEditValue(content);
    editValueRef.current = content;
    setIsEditDialogOpen(true);
  };

  // Focus when edit dialog opens
  useEffect(() => {
    if (isEditDialogOpen && editInputRef.current) {
      // Small delay to ensure dialog is rendered
      setTimeout(() => {
        editInputRef.current?.focus();
      }, 100);
    }
  }, [isEditDialogOpen]);

  const handleSaveEdit = () => {
    const currentValue = editValueRef.current.trim();
    if (currentValue && editIndex !== null) {
      const updatedNotes = [...notes];
      updatedNotes[editIndex] = { ...updatedNotes[editIndex], content: currentValue };
      setNotes(updatedNotes);
      setEditIndex(null);
      setEditValue('');
      editValueRef.current = '';
      setIsEditDialogOpen(false);
    }
  };

  const handleDeleteTodo = (index: number) => {
    setDeleteIndex(index);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteTodo = () => {
    if (deleteIndex !== null) {
      setNotes(notes.filter((_, i) => i !== deleteIndex));
      setDeleteIndex(null);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleToggleActive = (index: number) => {
    setNotes(notes.map((note, i) =>
      i === index ? { ...note, active: !note.active } : note
    ));
  };

  const handlePreEntry = async () => {
    const now = new Date().toISOString();

    // Ensure notes are sent with both content and active status
    const formattedNotes = notes.map(note => ({
      content: note.content,
      active: note.active
    }));
    const res = await mixPostV1('/api/v1/daily_behavioral_checklist', {
      account_number: id,
      condition: {
        mood,
        // plannedTrades,
        // plannedWindows,
        expectedHighPrice,
        expectedLowPrice,
        rrTarget,
        notes: formattedNotes,
        submittedAt: now,
        symbol: selectedSymbol
      },
      type: "daily"
    });

    if (res.status) {
      getPreEntryData();
      const plan: PreTradePlan = {
        mood,
        plannedTrades: Math.max(0, plannedTrades),
        plannedWindows: plannedWindows.filter(w => w.start && w.end),
        expectedHighPrice,
        expectedLowPrice,
        rrTarget: Math.max(0, rrTarget),
        notes: formattedNotes,
        submittedAt: new Date().toISOString(),
        symbol: selectedSymbol
      };
      onSave(plan);
    } else {
      console.error('Failed to save pre-entry data:', res);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-3 text-sm">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-gray-700">{t('TodayMood')}</span>
          <select className="rounded-md border border-gray-300 px-3 py-2 w-full" value={mood} onChange={e => setMood(e.target.value)}>
            {['Calm', 'Focused', 'Anxious', 'Euphoric', 'Stressed'].map(m => <option key={m}>{t(m)}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-700">{t("Symbol")}</span>
          <select className="rounded-md border border-gray-300 px-3 py-2 w-full" value={selectedSymbol} onChange={e => setSelectedSymbol(e.target.value)}>
            {['US30', 'S&P500', 'XAUUSD', 'BTCUSD'].map(s => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-700">{t('MinimumRRTarget')}</span>
          <NumberField label="" value={rrTarget} onChange={(n) => setRrTarget(n || 0)} step={0.1} />
          <span className="text-xs text-gray-500">{t("Requires")} ≥ {settings.minRRAllowed}</span>
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-gray-700">{t('ExpectedHIGHPriceofDay')}</span>
          <NumberField label="" value={Number(expectedHighPrice || 0)} onChange={(n) => setExpectedHighPrice(Math.max(0, Math.floor(n || 0)))} step={1} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-700">{t('ExpectedLOWPriceofDay')}</span>
          <NumberField label="" value={Number(expectedLowPrice || 0)} onChange={(n) => setExpectedLowPrice(Math.max(0, Math.floor(n || 0)))} step={1} />
        </label>
      </div>

      {/* <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-gray-700">{t('ExpectedMarketEntryTimes')}</span>
          <Button className='bg-[#E9B460] hover:bg-[#d1a154] !rounded-lg text-white' onClick={addWindow}><Plus className="w-4 h-4" />{t("AddWindow")}</Button>
        </div>
        <div className="space-y-2 mt-3">
          {plannedWindows.map((w, idx) => (
            <div key={idx} className="grid md:grid-cols-4 gap-2">
              <input placeholder="Label" className="rounded-md border border-gray-300 px-3 py-2" value={w.label || ''} onChange={e => setPlannedWindows(ws => ws.map((it, i) => i === idx ? { ...it, label: e.target.value } : it))} />
              <input type="time" className="rounded-md border border-gray-300 px-3 py-2" value={w.start} onChange={e => setPlannedWindows(ws => ws.map((it, i) => i === idx ? { ...it, start: e.target.value } : it))} />
              <input type="time" className="rounded-md border border-gray-300 px-3 py-2" value={w.end} onChange={e => setPlannedWindows(ws => ws.map((it, i) => i === idx ? { ...it, end: e.target.value } : it))} />
              <Button className="justify-center" onClick={() => removeWindow(idx)}><Trash2 className="w-4 h-4" />{t("Remove")}</Button>
            </div>
          ))}
        </div>
      </div> */}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-gray-700">{t('Notes')}</span>
          <div className="flex flex-col gap-2 max-h-40 overflow-y-auto mt-2">
            {notes.map((note: { content: string; active: boolean }, index: number) => (
              <div key={index} className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  checked={note.active}
                  onChange={() => handleToggleActive(index)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="flex-1">{note.content}</span>
                <button
                  className="!text-yellow-500 hover:!text-yellow-600"
                  onClick={() => handleEditTodo(index)}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  className="!text-red-500 hover:!text-red-600"
                  onClick={() => handleDeleteTodo(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-gray-700">{t('AddNote')}</span>
          <div className="flex gap-2">
            <input
              type="text"
              className="rounded-md border border-gray-300 px-3 py-2 flex-1"
              placeholder={t('EnterNewNote')}
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
            />
            <Button
              className="bg-default-color bg-[#E9B460] hover:bg-[#d1a154] text-white !rounded-lg"
              onClick={handleAddTodo}
            >
              {t('Add')}
            </Button>
          </div>
        </div>

        <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)}>
          <div className="flex flex-col gap-2">
            <span className="text-gray-700 font-medium">{t('EditNote')}</span>
            <input
              ref={editInputRef}
              type="text"
              className="rounded-md border border-gray-300 px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              defaultValue={editValue}
              onInput={(e) => {
                editValueRef.current = e.currentTarget.value;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveEdit();
                } else if (e.key === 'Escape') {
                  setIsEditDialogOpen(false);
                }
              }}
            />
            <div className="flex gap-2 justify-end w-full">
              <Button
                className="bg-gray-300 hover:bg-gray-400 text-white"
                onClick={() => setIsEditDialogOpen(false)}
              >
                {t('Cancel')}
              </Button>
              <Button
                className="bg-green-500 hover:bg-green-600 text-white"
                onClick={handleSaveEdit}
              >
                {t('Save')}
              </Button>
            </div>
          </div>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
          <div className="flex flex-col gap-2">
            <span className="text-gray-700 font-medium">{t('DeleteNote')}</span>
            <p>{t('DeleteNoteConfirm')}</p>
            <div className="flex gap-2 justify-end">
              <Button
                className="bg-gray-300 hover:bg-gray-400 text-white"
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                {t('Cancel')}
              </Button>
              <Button
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={confirmDeleteTodo}
              >
                {t('Delete')}
              </Button>
            </div>
          </div>
        </Dialog>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={handlePreEntry} className="px-6 py-2 bg-default-color bg-[#E9B460] hover:bg-[#d1a154] text-white !rounded-lg transition-colors duration-200 font-medium text-base">{t("Save")}</button>
        {value && <span className="text-xs text-gray-500 whitespace-nowrap">{t("Savedat")} {new Date(value.submittedAt).toLocaleTimeString()}</span>}
      </div>
    </div>
  );
}

const DetailsPlans = ({ key, item }: { key: any, item: PreTradePlan }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  function formatTo12Hour(timeString: string): string {
    return dayjs(timeString).format("h:mm:ss A");
  }

  return (
    <div key={key} className="flex p-2 mt-2 cts-pre-entry w-full cursor-pointer "
      onClick={() => {
        setIsModalOpen(true)
      }}>
      <Modal
        title="Pre-Trade Plan Details"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        afterOpenChange={(open) => {
          if (!open) setIsModalOpen(false);
        }}
        // Prevent click bubbling from modal content to parent div
        modalRender={modal => (
          <div onClick={e => e.stopPropagation()}>{modal}</div>
        )}
      >
        <div className="flex flex-col gap-2">
          <div className='w-full flex flex-row justify-center items-center whitespace-nowrap'>
            <p className='w-1/2 !mb-0 whitespace-nowrap'><strong>{t('SubmittedAt')}:</strong> {formatTo12Hour(item.condition.submittedAt || item.created_at)}</p>
            <p className='w-1/2 !mb-0 whitespace-nowrap'><strong>{t("Mood")}:</strong> {item.condition.mood}</p>
          </div>
          <div className='w-full flex flex-row justify-center items-center whitespace-nowrap'>
            <p className='w-1/2 !mb-0 whitespace-nowrap'><strong>{t("PlannedTrades")}:</strong> {item.condition.plannedTrades || item.condition.lots || 'N/A'}</p>
            <p className='w-1/2 !mb-0 whitespace-nowrap'><strong>{t("RRTarget")}:</strong> {item.condition.rrTarget || item.condition.rr || 'N/A'}</p>
          </div>
          <div>
            <strong>{t("PlannedWindows")}:</strong>
            <ul>
              {item.condition.plannedWindows.map((window: { label: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; start: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; end: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; }, index: React.Key | null | undefined) => (
                <li key={index} className="whitespace-nowrap">
                  {window.label} {window.start}-{window.end}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <strong>{t("Notes")}:</strong>
            {Array.isArray(item.condition.notes) && item.condition.notes.length > 0 ? (
              <ul>
                {item.condition.notes.map((note: { content: string; active: boolean }, index: number) => (
                  <li key={index} className="whitespace-nowrap">
                    • {note.content} {note.active ? '(Active)' : '(Inactive)'}
                  </li>
                ))}
              </ul>
            ) : (
              <p>{t("No notes available")}</p>
            )}
          </div>
        </div>
      </Modal>
      <div className="flex flex-row items-between gap-1.5 w-full">
        <div className="flex flex-col items-start gap-1.5 w-1/2">
          <p className="m-0 h-[20px] whitespace-nowrap">{formatTo12Hour(item.condition.submittedAt || item.created_at)}</p>
          <p className="m-0 h-[20px] whitespace-nowrap">{t('MinimumRRTarget')} &ge; {item.condition.rrTarget || item.condition.rr || 'N/A'}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5 w-1/2">
          <div className="flex flex-row items-center gap-1.5 content">
            <p className="m-0 text-[13px] h-[20px] text-gray whitespace-nowrap">
              {t("Trade")}: {item.condition.plannedTrades || item.condition.lots || 'N/A'}
            </p>
            <p className="m-0 text-[13px] h-[20px] text-gray whitespace-nowrap">{t("Mood")}: {item.condition.mood}</p>
          </div>
          <div className="flex items-center gap-1.5 content">
            <p className="m-0 text-[13px] h-[20px] text-gray flex gap-1 whitespace-nowrap">
              <span className=' whitespace-nowrap'>{t('Windows')}:</span>
              <p className="flex flex-col whitespace-nowrap">
                {item.condition.plannedWindows.map((window: { label: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; start: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; end: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; }, index: React.Key | null | undefined) => (
                  <span key={index} className="whitespace-nowrap">
                    {window.label} {window.start}-{window.end}
                  </span>
                ))}
              </p>
            </p>
          </div>

        </div>
      </div>
    </div>
  )
}

export { DetailsPlans };